import { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input/Input";
import { EyeIcon, EyeCloseIcon } from "@/icons";
import {
  checkPasswordBreachClient,
  validatePasswordFormat,
  getPasswordValidationMessage,
  type PasswordStrengthCheck,
} from "@/lib/passwordValidator";

export interface PasswordInputProps {
  id: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  autoComplete?: string;
  disabled?: boolean;
  error?: boolean;
  checkBreach?: boolean;
  onBreachCheckComplete?: (isPwned: boolean) => void;
}

export function PasswordInput({
  id,
  value,
  onChange,
  onBlur,
  placeholder = "Enter password",
  autoComplete = "current-password",
  disabled = false,
  error = false,
  checkBreach = false,
  onBreachCheckComplete,
}: PasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [breachCheck, setBreachCheck] = useState<PasswordStrengthCheck>({
    isChecking: false,
    isPwned: null,
    pwnedCount: null,
    error: null,
  });

  const checkPassword = useCallback(
    async (password: string) => {
      // Only check if breach checking is enabled and password meets minimum length
      if (!checkBreach || !password || password.length < 12) {
        setBreachCheck({
          isChecking: false,
          isPwned: null,
          pwnedCount: null,
          error: null,
        });
        return;
      }

      console.log(
        " Starting password breach check for password length:",
        password.length,
      );

      // Show loading state
      setBreachCheck((prev) => ({ ...prev, isChecking: true }));

      try {
        // Call API (will fallback to common passwords if API fails)
        console.log(" Calling checkPasswordBreachClient...");
        const result = await checkPasswordBreachClient(password);
        console.log(" Breach check result:", result);

        setBreachCheck({
          isChecking: false,
          isPwned: result.isPwned,
          pwnedCount: result.pwnedCount,
          error: result.error,
        });

        if (onBreachCheckComplete) {
          onBreachCheckComplete(result.isPwned);
        }
      } catch (err) {
        console.error("Breach check error:", err);
        setBreachCheck({
          isChecking: false,
          isPwned: null,
          pwnedCount: null,
          error: "Unable to verify password strength",
        });
      }
    },
    [checkBreach, onBreachCheckComplete],
  );

  useEffect(() => {
    if (!checkBreach) return;

    const timer = setTimeout(() => {
      void checkPassword(value);
    }, 500);

    return () => clearTimeout(timer);
  }, [value, checkBreach, checkPassword]);

  const passwordToggle = (
    <button
      type="button"
      onClick={() => setShowPassword((v) => !v)}
      className="cursor-pointer select-none"
      tabIndex={-1}
      aria-label={showPassword ? "Hide password" : "Show password"}
    >
      {showPassword ? (
        <EyeIcon className="fill-gray-500 dark:fill-gray-400" />
      ) : (
        <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400" />
      )}
    </button>
  );

  const formatResult = validatePasswordFormat(value);
  const validationMessage = getPasswordValidationMessage(
    formatResult,
    breachCheck,
  );

  return (
    <div className="space-y-2">
      <div className="relative">
        <Input
          id={id}
          type={showPassword ? "text" : "password"}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          autoComplete={autoComplete}
          disabled={disabled}
          error={error}
          rightElement={
            <div className="flex items-center gap-2">
              {breachCheck.isChecking && (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              )}
              {passwordToggle}
            </div>
          }
        />
      </div>

      {validationMessage && value && (
        <p
          className={`text-xs ${
            validationMessage.type === "error"
              ? "text-red-600 dark:text-red-400"
              : validationMessage.type === "warning"
                ? "text-yellow-600 dark:text-yellow-400"
                : "text-green-600 dark:text-green-400"
          }`}
        >
          {validationMessage.message}
        </p>
      )}
    </div>
  );
}
